import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  MegaphoneIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { SidebarSection } from '../../types';

/**
 * Sidebar for Admin Dashboard routes (`/admin/*`). Mirrors the pattern used
 * by `HostSidebar` / `ProfileSidebar` so it drops into `AdminLayout` the
 * same way. All links point to /admin/* routes.
 */
function buildSidebarSections(): SidebarSection[] {
  return [
    {
      titleKey: 'admin:sidebar.accountManagement',
      items: [
        { labelKey: 'admin:sidebar.users', path: '/admin/users', icon: UserGroupIcon },
        { labelKey: 'admin:sidebar.accountsAndRoles', path: '/admin/user-roles', icon: ShieldCheckIcon },
      ],
    },
    {
      titleKey: 'admin:sidebar.activityManagement',
      items: [
        { labelKey: 'admin:sidebar.campaignList', path: '/admin/campaigns', icon: ClipboardDocumentListIcon },
        { labelKey: 'admin:sidebar.campaignMatches', path: '/admin/matches', icon: UsersIcon },
        { labelKey: 'admin:sidebar.campaignManagers', path: '/admin/campaign-managers', icon: UserPlusIcon },
        { labelKey: 'admin:documentTemplates.sidebarLabel', path: '/admin/document-templates', icon: DocumentDuplicateIcon },
      ],
    },
    {
      titleKey: 'admin:sidebar.customerService',
      items: [
        { labelKey: 'admin:sidebar.notices', path: '/admin/notices', icon: MegaphoneIcon },
        { labelKey: 'admin:sidebar.inquiries', path: '/admin/inquiries', icon: ChatBubbleLeftRightIcon },
      ],
    },
    {
      titleKey: 'admin:sidebar.otherSettings',
      items: [
        { labelKey: 'admin:sidebar.settings', path: '/admin/settings', icon: Cog6ToothIcon },
      ],
    },
  ];
}

export interface AdminSidebarProps {
  variant?: 'standalone' | 'embedded';
  onItemClick?: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  variant = 'embedded',
  onItemClick,
}) => {
  const { t } = useTranslation(['admin', 'profile']);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();

  const sections = buildSidebarSections();

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const handleLogout = async () => {
    if (!window.confirm(t('profile:myProfile.sidebar.logoutConfirm', '로그아웃 하시겠습니까?'))) return;
    await logout();
    navigate('/login');
  };

  if (variant !== 'embedded') return null;

  return (
    <>
      {sections.map((section, idx) => (
        <div key={section.titleKey} className={idx > 0 ? 'mt-6' : ''}>
          <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t(section.titleKey)}
          </h3>
          <ul className="space-y-1">
            {section.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                    {t(item.labelKey)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <div className="mt-6">
        <ul>
          <li>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0 text-gray-400" />
              {t('admin:sidebar.logout')}
            </button>
          </li>
        </ul>
      </div>
    </>
  );
};

export default AdminSidebar;
